import { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { cn } from '@/lib/utils.ts'

/**
 * Zentrale 2D-Matrix-Komponente — Ordner/User/Abteilungen × Gruppen.
 *
 * Features:
 *   • 2-Level-Header (Typ-Mega-Header + Einzelspalten-Header)
 *   • Vertikale (90°) Einzelspalten-Header (opt-out)
 *   • Ein oder mehrere sticky linke Label-Spalten (`labelColumns`)
 *   • Sticky-Parent-Chain: bei Tree-Zeilen (rows mit parentId) bleibt die
 *     Ahnenreihe beim vertikalen Scroll als schmale Kontext-Bar oben.
 *   • Cell-Rendering als Render-Prop.
 */

export interface MatrixRow {
  id: string
  /** Kurzlabel fuer Sticky-Parent-Chain (Breadcrumb). Optional, default leer. */
  label?: ReactNode
  /** Einruecklevel fuer Tree-Darstellung (0 = Wurzel). */
  level?: number
  /** ID der Parent-Zeile, fuer Sticky-Parent-Chain. */
  parentId?: string | null
}

export interface MatrixColumn {
  id: string
  label: ReactNode
  /** Title-Tooltip beim Hover des Headers. */
  title?: string
  /** Optional feste Spaltenbreite in px. Default: 32. */
  widthPx?: number
  /** Node unterhalb des vertikalen Headers (z. B. "Alle"-Toggle oder Loeschen-Icon). */
  headerFooter?: ReactNode
}

export interface MatrixColumnGroup<C extends MatrixColumn = MatrixColumn> {
  id: string
  label: ReactNode
  /** Tailwind-Klassen fuer den Mega-Header (Hintergrund/Text). */
  headerClass?: string
  columns: C[]
}

export interface MatrixLabelColumn<R extends MatrixRow> {
  id: string
  /** Kopfzeile der Label-Spalte. */
  header: ReactNode
  /** Breite in px. */
  widthPx: number
  /** Renderer fuer die Zelle dieser Label-Spalte. */
  render: (row: R) => ReactNode
  /** Zusaetzliche Klassen auf der Zelle. */
  cellClassName?: string
  /** Wenn true: diese Label-Spalte bekommt links-padding nach row.level (Tree-Indent). */
  indented?: boolean
}

export interface MatrixTableProps<R extends MatrixRow, C extends MatrixColumn> {
  rows: R[]
  /** Eine oder mehrere sticky linke Label-Spalten. */
  labelColumns: MatrixLabelColumn<R>[]
  columnGroups: MatrixColumnGroup<C>[]
  renderCell: (row: R, column: C) => ReactNode

  /** Spalten-Header 90° gedreht darstellen. Default: true. */
  verticalHeaders?: boolean
  /** Hoehe in px, bei der die vertikalen Spalten-Header enden. Default: 100. */
  verticalHeaderHeight?: number
  /** Sticky-Parent-Chain aktivieren (braucht rows mit parentId). Default: false. */
  stickyAncestors?: boolean
  /** Max-Hoehe des Scroll-Containers (CSS value). Default: '70vh'. */
  maxHeight?: string
  /** Optional zusaetzliche Klassen am aeusseren Wrapper. */
  className?: string
  /** Fusszeilen-Slot unterhalb der Tabelle (z. B. Legende). */
  footer?: ReactNode
}

const DEFAULT_COL_WIDTH = 32
const HEADER_TYPE_HEIGHT = 28

export function MatrixTable<R extends MatrixRow, C extends MatrixColumn>({
  rows,
  labelColumns,
  columnGroups,
  renderCell,
  verticalHeaders = true,
  verticalHeaderHeight = 100,
  stickyAncestors = false,
  maxHeight = '70vh',
  className,
  footer,
}: MatrixTableProps<R, C>) {
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const rowRefs = useRef<Map<string, HTMLTableRowElement>>(new Map())
  const [visibleRowId, setVisibleRowId] = useState<string | null>(null)

  const rowIndex = useMemo(() => new Map(rows.map((r) => [r.id, r])), [rows])
  const flatColumns = useMemo(() => columnGroups.flatMap((g) => g.columns), [columnGroups])

  // Kumulierte left-Offsets pro Label-Spalte (fuer sticky-left-Stacking).
  const labelOffsets = useMemo(() => {
    const offsets: number[] = []
    let acc = 0
    for (const lc of labelColumns) {
      offsets.push(acc)
      acc += lc.widthPx
    }
    return { offsets, total: acc }
  }, [labelColumns])

  // Sticky-Ancestors: Track welche Zeile gerade oben unter dem Header steht.
  useEffect(() => {
    if (!stickyAncestors) return
    const container = scrollRef.current
    if (!container) return
    const observer = new IntersectionObserver(
      (entries) => {
        const topVisible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0]
        if (topVisible) {
          const id = (topVisible.target as HTMLElement).dataset.rowId
          if (id) setVisibleRowId(id)
        }
      },
      { root: container, threshold: 0, rootMargin: '-1px 0px 0px 0px' },
    )
    rowRefs.current.forEach((el) => observer.observe(el))
    return () => observer.disconnect()
  }, [stickyAncestors, rows])

  const ancestors: R[] = useMemo(() => {
    if (!stickyAncestors || !visibleRowId) return []
    const chain: R[] = []
    const current = rowIndex.get(visibleRowId)
    let parentId = current?.parentId ?? null
    while (parentId) {
      const parent = rowIndex.get(parentId)
      if (!parent) break
      chain.unshift(parent)
      parentId = parent.parentId ?? null
    }
    return chain
  }, [stickyAncestors, visibleRowId, rowIndex])

  useLayoutEffect(() => {
    if (!stickyAncestors) setVisibleRowId(null)
  }, [stickyAncestors, rows])

  const totalColSpan = labelColumns.length + flatColumns.length

  return (
    <div className={cn('bg-white border border-gray-200 rounded-lg shadow-card overflow-hidden', className)}>
      <div ref={scrollRef} className="overflow-auto relative" style={{ maxHeight }}>
        <table className="text-small border-separate border-spacing-0">
          <thead>
            {/* Ebene 1 — Label-Koepfe + Mega-Header fuer Datenspalten */}
            <tr className="align-bottom">
              {labelColumns.map((lc, i) => (
                <th
                  key={lc.id}
                  rowSpan={2}
                  className="sticky z-30 bg-gray-100 border border-gray-300 text-left px-3 py-2 font-semibold text-gray-700 align-middle"
                  style={{
                    width: lc.widthPx,
                    minWidth: lc.widthPx,
                    maxWidth: lc.widthPx,
                    left: labelOffsets.offsets[i],
                    top: 0,
                  }}
                >
                  {lc.header}
                </th>
              ))}
              {columnGroups.map((g) => (
                <th
                  key={g.id}
                  colSpan={g.columns.length}
                  className={cn(
                    'sticky top-0 z-20 border border-gray-300 px-2 py-1 text-center align-bottom text-small font-bold tracking-wide',
                    g.headerClass ?? 'bg-gray-100 text-gray-700',
                  )}
                >
                  {g.label}
                </th>
              ))}
            </tr>
            {/* Ebene 2 — Einzelspalten-Header */}
            <tr className="align-bottom">
              {columnGroups.flatMap((g) =>
                g.columns.map((c) => {
                  const w = c.widthPx ?? DEFAULT_COL_WIDTH
                  return (
                    <th
                      key={c.id}
                      className="sticky z-20 bg-gray-50 border border-gray-300 text-center align-bottom font-medium text-gray-700 px-0 pt-1 pb-2"
                      style={{ width: w, minWidth: w, maxWidth: w, top: HEADER_TYPE_HEIGHT }}
                      title={c.title}
                    >
                      {verticalHeaders ? (
                        <div className="flex items-end justify-center" style={{ minHeight: verticalHeaderHeight }}>
                          <div
                            className="text-[11px] whitespace-nowrap overflow-hidden"
                            style={{
                              writingMode: 'vertical-rl',
                              transform: 'rotate(180deg)',
                              maxHeight: verticalHeaderHeight,
                            }}
                          >
                            {c.label}
                          </div>
                        </div>
                      ) : (
                        <div className="text-[11px] px-1">{c.label}</div>
                      )}
                      {c.headerFooter ? (
                        <div className="flex flex-col items-center gap-0 mt-0.5">{c.headerFooter}</div>
                      ) : null}
                    </th>
                  )
                }),
              )}
            </tr>
            {/* Sticky-Parent-Chain unter dem Header */}
            {stickyAncestors && ancestors.length > 0 && (
              <tr className="align-middle">
                <th
                  colSpan={totalColSpan}
                  className="sticky z-10 bg-brand-50/80 backdrop-blur border-b border-brand-200 px-3 py-1 text-left"
                  style={{ top: HEADER_TYPE_HEIGHT + verticalHeaderHeight + 4 }}
                >
                  <div className="flex items-center gap-1 text-caption text-brand-800 font-medium">
                    {ancestors.map((a, i) => (
                      <Fragment key={a.id}>
                        {i > 0 && <span className="text-brand-400">›</span>}
                        <span className="truncate max-w-[240px]">{a.label ?? a.id}</span>
                      </Fragment>
                    ))}
                  </div>
                </th>
              </tr>
            )}
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr
                key={r.id}
                data-row-id={r.id}
                ref={(el) => {
                  if (el) rowRefs.current.set(r.id, el)
                  else rowRefs.current.delete(r.id)
                }}
                className="hover:bg-gray-50/40"
              >
                {labelColumns.map((lc, i) => (
                  <td
                    key={lc.id}
                    className={cn(
                      'sticky z-10 bg-white border border-gray-200 px-3 py-1.5 text-gray-800 align-middle',
                      lc.cellClassName,
                    )}
                    style={{
                      width: lc.widthPx,
                      minWidth: lc.widthPx,
                      maxWidth: lc.widthPx,
                      left: labelOffsets.offsets[i],
                      paddingLeft: lc.indented ? 12 + (r.level ?? 0) * 14 : undefined,
                    }}
                  >
                    {lc.render(r)}
                  </td>
                ))}
                {flatColumns.map((c) => {
                  const w = c.widthPx ?? DEFAULT_COL_WIDTH
                  return (
                    <td
                      key={c.id}
                      className="border border-gray-200 text-center align-middle p-0"
                      style={{ width: w, minWidth: w, maxWidth: w }}
                    >
                      {renderCell(r, c)}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {footer ? <div className="border-t border-gray-200 bg-gray-50 px-3 py-2">{footer}</div> : null}
    </div>
  )
}
